/**
 * Admin utility hooks and capability-gated screen wrapper for PSI.
 *
 * `<RestrictedScreen>` checks the current user's access status via
 * `useAccessCheck` and renders `<Unauthorized>` or `<Forbidden>` before any
 * protected content is shown. Legal doc link helpers
 * (`getDefaultCommunityGuidelinesLink`, etc.) fall back to
 * `psi.newpublic.org/pages/<siloKey>/...` unless the silo has overridden them
 * via settings.
 */

import { type LocalizedString, resolveLocalizedString } from '@repo/shared/localized-string';
import { useSettings } from 'data/settings';
import { useAccessCheck } from 'util/admin';

import { Forbidden } from './errors/forbidden';
import { Unauthorized } from './errors/unauthorized';
import { useLanguage } from './translation';

// Link generators for guidelines, privacy, and terms
/** @public */
export const getDefaultCommunityGuidelinesLink = (siloKey: string) => {
    return `https://psi.newpublic.org/pages/${siloKey}/community-guidelines.html`;
};

/** @public */
export const getDefaultPrivacyNoticeLink = (siloKey: string) => {
    return `https://psi.newpublic.org/pages/${siloKey}/privacy-notice.html`;
};

/** @public */
export const getDefaultTermsOfServiceLink = (siloKey: string) => {
    return `https://psi.newpublic.org/pages/${siloKey}/terms-of-service.html`;
};

export type RestrictedScreenProps = { capability?: string; children: React.ReactNode };

export function RestrictedScreen({ capability, children }: RestrictedScreenProps) {
    const { status } = useAccessCheck(capability);

    if (status === 'unauthorized') return <Unauthorized />;
    if (status === 'forbidden') return <Forbidden />;
    return children;
}

/** @public */
export type LegalDocLinks = {
    communityGuidelines?: LocalizedString;
    privacyNotice?: LocalizedString;
    termsOfService?: LocalizedString;
};

export type LegalDocLinksWithSuffix = {
    communityGuidelinesLink: string;
    privacyNoticeLink: string;
    termsOfServiceLink: string;
};

export function useSiloName(): string | undefined {
    const settings = useSettings();
    const language = useLanguage();
    if (!settings) return undefined;
    return resolveLocalizedString(settings.siloName ?? undefined, language);
}

export function useShowCollaborationNote(): boolean | undefined {
    return useSettings()?.showCollaborationNote;
}

/**
 * Returns legal doc links, falling back to defaults if not set in settings.
 * When a configured value is a per-language map, the link is resolved for the
 * current UI language using the dialect → primary subtag → en-US fallback
 * shared with the server-side email-template resolution.
 */
export function useLegalDocLinks(siloKey: string): LegalDocLinksWithSuffix {
    const configuredLegalDocs = useSettings()?.legalDocLinks;
    const language = useLanguage();
    return {
        communityGuidelinesLink:
            resolveLocalizedString(configuredLegalDocs?.communityGuidelines, language) ?? getDefaultCommunityGuidelinesLink(siloKey),
        privacyNoticeLink: resolveLocalizedString(configuredLegalDocs?.privacyNotice, language) ?? getDefaultPrivacyNoticeLink(siloKey),
        termsOfServiceLink: resolveLocalizedString(configuredLegalDocs?.termsOfService, language) ?? getDefaultTermsOfServiceLink(siloKey),
    };
}

export function useIsBeta(): boolean | undefined {
    return useSettings()?.isBeta;
}
